import {
  format,
  isSameMonth,
  isThisMonth,
  parseISO,
  startOfMonth,
  subMonths,
} from "date-fns";
import { ptBR } from "date-fns/locale";

export type AttachmentDirection = "inbound" | "outbound";

export interface RawConversationMessage {
  id: string;
  body: string | null;
  from_user: string;
  created_at: string;
  conversation_id: string;
  metadata: Record<string, unknown> | null;
}

export interface MediaAttachmentItem {
  kind: "media";
  id: string;
  messageId: string;
  createdAt: string;
  direction: AttachmentDirection;
  mediaType: string;
  url: string;
  label: string;
  thumbnailUrl?: string;
}

export interface DocAttachmentItem {
  kind: "doc";
  id: string;
  messageId: string;
  createdAt: string;
  direction: AttachmentDirection;
  url: string;
  fileName: string;
}

export interface LinkAttachmentItem {
  kind: "link";
  id: string;
  messageId: string;
  createdAt: string;
  direction: AttachmentDirection;
  url: string;
  domain: string;
  preview: string;
}

export type AttachmentItem = MediaAttachmentItem | DocAttachmentItem | LinkAttachmentItem;

export interface AttachmentMonthGroup<T extends AttachmentItem> {
  label: string;
  items: T[];
}

const MEDIA_TYPES = new Set(["image", "video", "audio", "share", "story_mention"]);
const URL_REGEX = /https?:\/\/[^\s<>"')\]]+/gi;
const CDN_HOST_PATTERNS = [
  "lookaside.fbsbx.com",
  "scontent",
  "fbcdn.net",
  "mmg.whatsapp.net",
  "pps.whatsapp.net",
];

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function getMediaRecord(meta: Record<string, unknown>): Record<string, unknown> | null {
  return asRecord(meta.media) ?? asRecord(meta);
}

export function getMessageDirection(fromUser: string): AttachmentDirection {
  return fromUser === "assistant" || fromUser === "system" ? "outbound" : "inbound";
}

function getResolvableMediaUrl(meta: Record<string, unknown>): string | null {
  if (meta.media_unavailable) return null;
  const media = getMediaRecord(meta);
  if (!media) return null;
  const url = typeof media.url === "string" ? media.url : null;
  if (url) return url;
  const base64 = typeof media.base64 === "string" ? media.base64 : null;
  if (!base64) return null;
  const mime =
    (typeof media.mimeType === "string" && media.mimeType) ||
    (typeof media.mime_type === "string" && media.mime_type) ||
    "application/octet-stream";
  return `data:${mime};base64,${base64}`;
}

function isCdnMediaUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return CDN_HOST_PATTERNS.some((p) => host.includes(p));
  } catch {
    return false;
  }
}

function isMediaPlaceholderBody(body: string, type: string): boolean {
  const t = body.trim();
  if (!t) return true;
  if (type === "image") {
    return (
      t === "[Imagem]" ||
      t === "[imagem]" ||
      t.toLowerCase() === "imagem" ||
      /^\[Imagem/i.test(t) ||
      /\.(png|jpg|jpeg|webp|gif)$/i.test(t)
    );
  }
  if (type === "video") {
    return t === "[Vídeo]" || t === "[Video]" || /^\[V[ií]deo/i.test(t);
  }
  if (type === "document") {
    return t === "[Documento]" || t === "[Documento enviado]";
  }
  if (type === "share") {
    return t === "[Conteúdo compartilhado]" || isCdnMediaUrl(t);
  }
  if (type === "story_mention") {
    return t === "[Menção no Story]";
  }
  return false;
}

export function extractUrlsFromText(text: string): string[] {
  const matches = text.match(URL_REGEX) ?? [];
  const seen = new Set<string>();
  const urls: string[] = [];
  for (let raw of matches) {
    raw = raw.replace(/[.,;:!?)]+$/, "");
    if (!raw || seen.has(raw)) continue;
    seen.add(raw);
    urls.push(raw);
  }
  return urls;
}

function mediaLabel(type: string): string {
  switch (type) {
    case "image":
      return "Imagem";
    case "video":
      return "Vídeo";
    case "audio":
      return "Áudio";
    case "share":
      return "Compartilhado";
    case "story_mention":
      return "Story";
    default:
      return "Mídia";
  }
}

export function classifyMessageAttachments(msg: RawConversationMessage): AttachmentItem[] {
  const meta = asRecord(msg.metadata) ?? {};
  const type = typeof meta.type === "string" ? meta.type : "";
  const direction = getMessageDirection(msg.from_user);
  const createdAt = msg.created_at;
  const body = msg.body ?? "";
  const items: AttachmentItem[] = [];

  if (MEDIA_TYPES.has(type)) {
    const url =
      getResolvableMediaUrl(meta) ||
      (typeof meta.story_url === "string" ? meta.story_url : null) ||
      (typeof meta.share_url === "string" ? meta.share_url : null);
    if (url) {
      items.push({
        kind: "media",
        id: `${msg.id}-media`,
        messageId: msg.id,
        createdAt,
        direction,
        mediaType: type,
        url,
        label: mediaLabel(type),
        thumbnailUrl: type === "image" || type === "video" ? url : undefined,
      });
    }
    return items;
  }

  if (type === "document") {
    const url = getResolvableMediaUrl(meta);
    if (url) {
      const media = getMediaRecord(meta);
      const fileName =
        (typeof media?.fileName === "string" && media.fileName) ||
        (typeof meta.fileName === "string" && meta.fileName) ||
        "Documento";
      items.push({
        kind: "doc",
        id: `${msg.id}-doc`,
        messageId: msg.id,
        createdAt,
        direction,
        url,
        fileName,
      });
    }
    return items;
  }

  if (isMediaPlaceholderBody(body, type)) return items;

  const urls = extractUrlsFromText(body).filter((u) => !isCdnMediaUrl(u));
  urls.forEach((url, index) => {
    let domain = url;
    try {
      domain = new URL(url).hostname.replace(/^www\./, "");
    } catch {
      /* keep raw */
    }
    const preview = body.replace(url, "").trim().slice(0, 80) || domain;
    items.push({
      kind: "link",
      id: `${msg.id}-link-${index}`,
      messageId: msg.id,
      createdAt,
      direction,
      url,
      domain,
      preview,
    });
  });

  return items;
}

export function partitionAttachments(messages: RawConversationMessage[]): {
  media: MediaAttachmentItem[];
  links: LinkAttachmentItem[];
  docs: DocAttachmentItem[];
} {
  const media: MediaAttachmentItem[] = [];
  const links: LinkAttachmentItem[] = [];
  const docs: DocAttachmentItem[] = [];

  for (const msg of messages) {
    for (const item of classifyMessageAttachments(msg)) {
      if (item.kind === "media") media.push(item);
      else if (item.kind === "doc") docs.push(item);
      else links.push(item);
    }
  }

  const byDateDesc = <T extends { createdAt: string }>(a: T, b: T) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

  media.sort(byDateDesc);
  links.sort(byDateDesc);
  docs.sort(byDateDesc);

  return { media, links, docs };
}

function monthGroupLabel(date: Date, now: Date): string {
  if (isThisMonth(date)) return "Neste mês";
  if (isSameMonth(date, subMonths(now, 1))) return "Mês passado";
  const label = format(date, "MMMM yyyy", { locale: ptBR });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function groupAttachmentsByMonth<T extends AttachmentItem>(
  items: T[],
): AttachmentMonthGroup<T>[] {
  if (items.length === 0) return [];

  const now = new Date();
  const map = new Map<string, T[]>();

  for (const item of items) {
    const date = parseISO(item.createdAt);
    const key = format(startOfMonth(date), "yyyy-MM");
    const label = monthGroupLabel(date, now);
    const existing = map.get(key);
    if (existing) existing.push(item);
    else map.set(key, [item]);
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([, groupItems]) => ({
      label: monthGroupLabel(parseISO(groupItems[0].createdAt), now),
      items: groupItems,
    }));
}

export function countLabel(kind: "media" | "link" | "doc", count: number): string {
  if (count === 0) return "";
  if (kind === "media") return `${count} ${count === 1 ? "item" : "itens"} de mídia`;
  if (kind === "link") return `${count} ${count === 1 ? "link" : "links"}`;
  return `${count} ${count === 1 ? "documento" : "documentos"}`;
}
